/**
 * The live trips board: everything about it that is decidable without a map.
 *
 * Ported from the standalone railcrew-trip-board page. Every rule that turns a row into something
 * a dispatcher reads -- is it late, what colour is it, which chips does it count under, does it
 * survive the current filters -- lives here rather than in the component, because these are the
 * parts that are worth pinning down in tests and the parts most likely to drift.
 *
 * The row shape is the board query's own column names (tripID, trip_status, is_late,
 * current_loc_source, ...). They are not renamed on the way through OrderApi or the Flow BFF, so
 * they are not renamed here either -- both halves of this feature are written against the SQL.
 */

export type Leg = "pending" | "to_pickup" | "to_dropoff" | "return" | "dropoff";

/** One open trip. Column names are the board query's; see queries/active_upcoming_trips.sql. */
export interface LiveBoardTrip {
  tripID: number;
  tripNumber?: string;
  trainNumber?: string;
  trip_type?: string;
  client?: string;
  is_asap?: boolean;
  planned_miles?: number;
  pickup_local?: string;
  trip_tz?: string;
  pickup_utc?: string;
  minutes_to_pickup?: number;
  trip_status?: string;
  dispatch_status?: string;
  begin_trip_utc?: string;
  arrived_pickup_utc?: string;
  pickup_completed_utc?: string;
  crew_dropped_utc?: string;
  leg: Leg;

  is_late?: number;
  late_reason?: "arrived_late" | "not_arrived" | null;
  late_minutes?: number;
  drive_minutes_to_pickup?: number;
  drive_minutes_source?: string;
  must_start_by_utc?: string;
  is_potential_late?: number;

  backhaul_hub_count?: number;
  backhaul_nearest_min?: number;
  backhaul_hubs?: string;
  backhaul_hub_ids?: string;

  last_event_id?: number;
  last_event_type_id?: number;
  last_event?: string;
  last_event_utc?: string;
  minutes_since_last_event?: number;
  last_event_route_location?: string;

  driverID?: number;
  driver_name?: string;
  driver_employee_number?: string;
  driver_cell?: string;

  vehicleID?: number;
  vehicle_unit?: string;
  vehicle_description?: string;
  vehicle_plate?: string;
  vehicle_type?: string;

  hubID: number;
  hub?: string;
  hub_state?: string;
  hub_lat?: number;
  hub_lon?: number;

  vehicle_lat?: number;
  vehicle_lon?: number;
  vehicle_gps_utc?: string;
  vehicle_gps_age_min?: number;
  vehicle_last_movement_utc?: string;
  vehicle_stopped_min?: number;

  pickUpPointID?: number;
  pickup_name?: string;
  pickup_city?: string;
  pickup_state?: string;
  pickup_lat?: number;
  pickup_lon?: number;
  pickup_address?: string;

  dropOffPointID?: number;
  dropoff_name?: string;
  dropoff_city?: string;
  dropoff_state?: string;
  dropoff_lat?: number;
  dropoff_lon?: number;
  dropoff_address?: string;

  current_lat?: number;
  current_lon?: number;
  current_loc_source?: string;
  current_loc_utc?: string;

  /** Filled by scrubCoords: the coordinate pairs that were dropped for being outside the US. */
  bad_coords?: string[];
}

export interface LiveBoardHubRow {
  hubID: number;
  hub?: string;
  hub_lat?: number;
  hub_lon?: number;
  hub_state?: string;
}

/**
 * The BFF envelope. Camel-cased because Flow's own serializer produced it; the rows inside keep
 * the board query's column names, which the BFF forwards verbatim.
 */
export interface LiveBoardResponse {
  snapshotUtc: string;
  hoursAhead: number;
  trips: LiveBoardTrip[];
  hubs: LiveBoardHubRow[];
}

/** A hub as the map draws it: coordinates known good, with how many trips sit on it. */
export interface BoardHub {
  id: string;
  name: string;
  lat: number;
  lon: number;
  trips: number;
}

/** Where the vehicle is heading -- or, for a pending trip, where it is waiting. */
export interface TripTarget {
  lat: number;
  lon: number;
  kind: "pickup" | "dropoff" | "hub";
}

export const LEG_LABEL: Record<Leg, string> = {
  pending: "Pending",
  to_pickup: "To pickup",
  to_dropoff: "To dropoff",
  return: "Returning to hub",
  dropoff: "Ended",
};

/** pending -> to_pickup -> to_dropoff -> return -> dropoff. The order the trip lives in. */
export const LEG_ORDER: Leg[] = ["pending", "to_pickup", "to_dropoff", "return", "dropoff"];

/** A trip that has logged End Trip is sitting at its dropoff, so it reads as the dropoff leg. */
export const legOf = (t: LiveBoardTrip): Exclude<Leg, "dropoff"> =>
  (t.leg === "dropoff" ? "to_dropoff" : t.leg) as Exclude<Leg, "dropoff">;

/** Vehicle GPS older than this stops being the trip's position and starts being a warning. */
export const STALE_MIN = 30;
/** Started, and neither a GPS fix nor an event for this long: the trip has gone quiet. */
export const SILENT_MIN = 90;

export const isOverdue = (t: LiveBoardTrip): boolean => t.trip_status === "Not Started - Past Pickup";
export const isLate = (t: LiveBoardTrip): boolean => t.is_late === 1 || isOverdue(t);
/** Late because the van is still en route past the pickup time -- late_minutes keeps growing. */
export const notArrived = (t: LiveBoardTrip): boolean => t.is_late === 1 && t.late_reason === "not_arrived";
export const isPotLate = (t: LiveBoardTrip): boolean => t.is_potential_late === 1 && !isOverdue(t);
export const hasBackhaul = (t: LiveBoardTrip): boolean => (t.backhaul_hub_count || 0) > 0;
export const isStale = (t: LiveBoardTrip): boolean =>
  t.leg !== "pending" && (t.vehicle_gps_age_min == null || t.vehicle_gps_age_min > STALE_MIN);
export const isSilent = (t: LiveBoardTrip): boolean =>
  t.leg !== "pending" &&
  (t.vehicle_gps_age_min == null || t.vehicle_gps_age_min > SILENT_MIN) &&
  (t.minutes_since_last_event == null || t.minutes_since_last_event > SILENT_MIN);

/** What a trip still needs before it can run. */
export const missing = (t: LiveBoardTrip): string[] =>
  [t.driverID == null ? "driver" : null, t.vehicleID == null ? "vehicle" : null].filter(Boolean) as string[];

export const backhaulHubIds = (t: LiveBoardTrip): string[] =>
  t.backhaul_hub_ids ? String(t.backhaul_hub_ids).split(";") : [];

// ---------------------------------------------------------------------------------------------
// Coordinate sanity
// ---------------------------------------------------------------------------------------------

/**
 * Anything outside the continental US is not a place this board can draw. (0, 0) is the usual
 * case -- an unset lat/lon pair -- and plotting it would put a van in the Gulf of Guinea and drag
 * every "fit to trips" zoom out to the Atlantic with it.
 */
export const inConus = (lon: number, lat: number): boolean =>
  lat >= 24 && lat <= 50 && lon >= -125.5 && lon <= -66;

const COORD_PAIRS: [string, keyof LiveBoardTrip, keyof LiveBoardTrip][] = [
  ["current", "current_lat", "current_lon"],
  ["pickup", "pickup_lat", "pickup_lon"],
  ["dropoff", "dropoff_lat", "dropoff_lon"],
  ["hub", "hub_lat", "hub_lon"],
];

/**
 * Drops every coordinate pair that is not in the continental US, remembering what was dropped.
 *
 * Treated as missing rather than as an error: the trip stays on the board and in the list, the
 * mark is simply not drawn and the zoom does not see it. The detail card lists what was thrown
 * out, because a van with no position is usually a data problem someone has to go fix.
 */
export const scrubCoords = (t: LiveBoardTrip): LiveBoardTrip => {
  const out: LiveBoardTrip = { ...t, bad_coords: [] };
  COORD_PAIRS.forEach(([what, la, lo]) => {
    const lat = out[la] as number;
    const lon = out[lo] as number;
    if (lat != null && lon != null && !inConus(+lon, +lat)) {
      out.bad_coords.push(`${what} ${(+lat).toFixed(3)}, ${(+lon).toFixed(3)}`);
      (out[la] as number) = null;
      (out[lo] as number) = null;
    }
  });
  return out;
};

export const targetOf = (t: LiveBoardTrip): TripTarget | null => {
  if (t.leg === "pending" || t.leg === "to_pickup") {
    return t.pickup_lat != null ? { lat: t.pickup_lat, lon: t.pickup_lon, kind: "pickup" } : null;
  }
  // Empty van heading back; the hub diamond is already on the map, so it is the marker.
  if (t.leg === "return") {
    return t.hub_lat != null ? { lat: t.hub_lat, lon: t.hub_lon, kind: "hub" } : null;
  }
  return t.dropoff_lat != null ? { lat: t.dropoff_lat, lon: t.dropoff_lon, kind: "dropoff" } : null;
};

/** The van's own position, once it has one. Pending trips are drawn at their pickup instead. */
export const currentPointOf = (t: LiveBoardTrip): [number, number] | null =>
  t.leg !== "pending" && t.current_lat != null ? [t.current_lon, t.current_lat] : null;

// ---------------------------------------------------------------------------------------------
// Severity: does this trip need attention
// ---------------------------------------------------------------------------------------------

export type Severity = "ok" | "warn" | "late";

/**
 * One colour per trip: red late, amber warning, otherwise the leg's own calm colour.
 *
 * A returning van is empty -- the crew has already been delivered -- so nothing about being late
 * still applies to it. Only going silent is worth an amber there.
 */
export const severityOf = (t: LiveBoardTrip): Severity => {
  const leg = legOf(t);
  if (leg === "return") return isSilent(t) ? "warn" : "ok";
  if (isLate(t)) return "late";
  if (leg === "pending") return isPotLate(t) || missing(t).length ? "warn" : "ok";
  return isPotLate(t) || isStale(t) ? "warn" : "ok";
};

/** The reasons behind severityOf, most important first, spelled out for the tooltip. */
export const reasonsFor = (t: LiveBoardTrip): string[] => {
  const leg = legOf(t);
  const r: string[] = [];
  if (leg === "return") {
    if (isSilent(t)) r.push(`silent · no GPS fix or event for ${SILENT_MIN}+ min`);
    return r;
  }
  if (isOverdue(t)) r.push(`pickup time was ${fmtRel(t.minutes_to_pickup)} and the trip never started`);
  else if (notArrived(t)) r.push(`pickup time passed ${t.late_minutes} min ago and the van has not arrived`);
  else if (isLate(t)) r.push(`arrived at pickup ${t.late_minutes} min late`);
  if (isPotLate(t)) r.push(`potential late · had to leave by ${fmtLocal(t.must_start_by_utc)} UTC`);
  if (leg === "pending") missing(t).forEach((m) => r.push(`no ${m} assigned`));
  else if (isSilent(t)) r.push(`silent · no GPS fix or event for ${SILENT_MIN}+ min`);
  else if (isStale(t)) {
    r.push(
      t.vehicle_gps_age_min == null
        ? "no GPS fix on record"
        : `GPS fix is ${fmtAgo(t.vehicle_gps_age_min)} (> ${STALE_MIN} min)`
    );
  }
  return r;
};

// ---------------------------------------------------------------------------------------------
// Filters
// ---------------------------------------------------------------------------------------------

/** The trip-status chips. Pending is split by what the trip still lacks. */
export const ALL_STATUSES = [
  "to_pickup",
  "to_dropoff",
  "return",
  "pending_ok",
  "pending_nodriver",
  "pending_novehicle",
] as const;
export type StatusKey = (typeof ALL_STATUSES)[number];

/** A pending trip missing both a driver and a vehicle counts under both chips. */
export const statusesOf = (t: LiveBoardTrip): StatusKey[] => {
  const leg = legOf(t);
  if (leg !== "pending") return [leg as StatusKey];
  const m = missing(t);
  return m.length ? (m.map((x) => (x === "driver" ? "pending_nodriver" : "pending_novehicle")) as StatusKey[]) : ["pending_ok"];
};

export type SortKey = "pickup" | "event" | "gps" | "hub";
export type FlagKey = "late" | "potlate" | "silent" | "stale" | "backhaul";

/**
 * The whole filter state.
 *
 * `hubs` and `clients` are null when nothing has been picked, which is not the same as "every
 * value ticked": null follows the default, so a client that first appears on the board after a
 * refresh is included (or, for the default-hidden ones, excluded) without the user re-picking.
 */
export interface BoardFilters {
  statuses: Set<StatusKey>;
  hubs: Set<string> | null;
  clients: Set<string> | null;
  flags: Set<FlagKey>;
  query: string;
  hours: number;
  sort: SortKey;
}

/** Internal, non-customer work. Unchecked on load; Select all brings it in. */
export const CLIENTS_HIDDEN_BY_DEFAULT = ["railcrew xpress"];

export const isHiddenByDefaultClient = (client: string): boolean =>
  CLIENTS_HIDDEN_BY_DEFAULT.indexOf((client || "").trim().toLowerCase()) >= 0;

export const defaultFilters = (): BoardFilters => ({
  statuses: new Set<StatusKey>(ALL_STATUSES),
  hubs: null,
  clients: null,
  flags: new Set<FlagKey>(),
  query: "",
  hours: 4,
  sort: "pickup",
});

/** Started trips always show; pending ones only inside the Upcoming window. */
export const inHorizon = (t: LiveBoardTrip, hours: number): boolean =>
  t.leg !== "pending" || t.minutes_to_pickup <= hours * 60;

/** Own hub selected, or the trip could backhaul into a selected hub. */
export const inHubs = (t: LiveBoardTrip, hubs: Set<string>): boolean =>
  hubs.has(String(t.hubID)) || backhaulHubIds(t).some((id) => hubs.has(id));

/** On the board only because it could backhaul INTO a selected hub -- worth saying so in the list. */
export const viaBackhaul = (t: LiveBoardTrip, hubs: Set<string> | null): boolean =>
  !!hubs && !hubs.has(String(t.hubID)) && backhaulHubIds(t).some((id) => hubs.has(id));

export const clientSelected = (f: BoardFilters, client: string): boolean =>
  f.clients ? f.clients.has(client || "") : !isHiddenByDefaultClient(client);

export const matchesQuery = (t: LiveBoardTrip, q: string): boolean => {
  const haystack = `${t.tripID} ${t.tripNumber || ""} ${t.driver_name || ""} ${t.vehicle_unit || ""} ${
    t.trainNumber || ""
  } ${t.hub || ""} ${t.client || ""} ${t.pickup_name || ""} ${t.dropoff_name || ""}`.toLowerCase();
  return haystack.indexOf(q) >= 0;
};

/** Which filter to look past. Faceted counts are built by skipping the facet being counted. */
export type SkipFilter = "hubs" | "clients" | "status";

export const passes = (t: LiveBoardTrip, f: BoardFilters, skip?: SkipFilter): boolean => {
  if (!inHorizon(t, f.hours)) return false;
  if (skip !== "status" && !statusesOf(t).some((s) => f.statuses.has(s))) return false;
  if (skip !== "hubs" && f.hubs && !inHubs(t, f.hubs)) return false;
  if (skip !== "clients" && !clientSelected(f, t.client)) return false;
  // Several flags selected = any of them; the flag group intersects with the status group.
  if (skip !== "status" && f.flags.size && !KPI_ITEMS.some((i) => i.flag && f.flags.has(i.flag) && i.match(t))) {
    return false;
  }
  if (f.query && !matchesQuery(t, f.query)) return false;
  return true;
};

const compareText = (a: string, b: string) => (a || "").localeCompare(b || "");

/** Oldest first, with anything undated pushed to the end rather than to the top. */
const compareTime = (a: string, b: string) => (timeValue(a) ?? Infinity) - (timeValue(b) ?? Infinity);

const SORTERS: Record<SortKey, (a: LiveBoardTrip, b: LiveBoardTrip) => number> = {
  pickup: (a, b) => compareTime(a.pickup_utc, b.pickup_utc),
  // Most recent event first; a trip with no events yet is the least recent thing on the board.
  event: (a, b) => (timeValue(b.last_event_utc) ?? -Infinity) - (timeValue(a.last_event_utc) ?? -Infinity),
  gps: (a, b) => (b.vehicle_gps_age_min ?? 1e9) - (a.vehicle_gps_age_min ?? 1e9),
  hub: (a, b) => compareText(a.hub, b.hub) || compareTime(a.pickup_utc, b.pickup_utc),
};

export const sortTrips = (trips: LiveBoardTrip[], sort: SortKey): LiveBoardTrip[] =>
  [...trips].sort(SORTERS[sort]);

export const visibleTrips = (trips: LiveBoardTrip[], f: BoardFilters): LiveBoardTrip[] =>
  sortTrips(trips.filter((t) => passes(t, f)), f.sort);

export const horizonCount = (trips: LiveBoardTrip[], hours: number): number =>
  trips.filter((t) => inHorizon(t, hours)).length;

// ---------------------------------------------------------------------------------------------
// Faceted counts
// ---------------------------------------------------------------------------------------------

export interface HubFacet {
  own: number;
  backhaul: number;
}

/**
 * Hub counts for the hub picker, following every filter EXCEPT the hub picker itself.
 *
 * That is what makes the numbers describe the current selection rather than the whole board:
 * pick a client and the hub list says how many of that client's trips sit at each hub.
 */
export const hubFacetCounts = (trips: LiveBoardTrip[], f: BoardFilters): Map<string, HubFacet> => {
  const counts = new Map<string, HubFacet>();
  const bump = (id: string, key: keyof HubFacet) => {
    const c = counts.get(id) || { own: 0, backhaul: 0 };
    c[key] += 1;
    counts.set(id, c);
  };
  trips.forEach((t) => {
    if (!passes(t, f, "hubs")) return;
    const own = String(t.hubID);
    bump(own, "own");
    backhaulHubIds(t).forEach((id) => {
      if (id !== own) bump(id, "backhaul");
    });
  });
  return counts;
};

export const clientFacetCounts = (trips: LiveBoardTrip[], f: BoardFilters): Map<string, number> => {
  const counts = new Map<string, number>();
  trips.forEach((t) => {
    if (!passes(t, f, "clients")) return;
    const c = t.client || "";
    counts.set(c, (counts.get(c) || 0) + 1);
  });
  return counts;
};

// ---------------------------------------------------------------------------------------------
// KPI chips
// ---------------------------------------------------------------------------------------------

export interface KpiItem {
  group: "Trip status" | "Warnings" | "Other";
  key: string;
  label: string;
  match: (t: LiveBoardTrip) => boolean;
  /** Status chips narrow F.statuses; flag chips toggle F.flags. Exactly one of the two is set. */
  status?: StatusKey;
  flag?: FlagKey;
  /** Which leg glyph to draw beside the count, when it differs from the status key. */
  glyph?: Leg;
  tone?: "crit" | "warn";
}

export const KPI_ITEMS: KpiItem[] = [
  { group: "Trip status", key: "to_pickup", label: "to pickup", match: (t) => t.leg === "to_pickup", status: "to_pickup" },
  {
    group: "Trip status",
    key: "to_dropoff",
    label: "to dropoff",
    match: (t) => t.leg === "to_dropoff" || t.leg === "dropoff",
    status: "to_dropoff",
  },
  { group: "Trip status", key: "return", label: "returning", match: (t) => t.leg === "return", status: "return" },
  {
    group: "Trip status",
    key: "pending_ok",
    label: "pending (assigned)",
    match: (t) => t.leg === "pending" && !missing(t).length,
    status: "pending_ok",
    glyph: "pending",
  },
  {
    group: "Trip status",
    key: "pending_nodriver",
    label: "pending (no driver)",
    match: (t) => t.leg === "pending" && t.driverID == null,
    status: "pending_nodriver",
    glyph: "pending",
    tone: "warn",
  },
  {
    group: "Trip status",
    key: "pending_novehicle",
    label: "pending (no vehicle)",
    match: (t) => t.leg === "pending" && t.vehicleID == null,
    status: "pending_novehicle",
    glyph: "pending",
    tone: "warn",
  },
  { group: "Warnings", key: "late", label: "late", match: isLate, flag: "late", tone: "crit" },
  { group: "Warnings", key: "potlate", label: "potential late", match: isPotLate, flag: "potlate", tone: "warn" },
  {
    group: "Warnings",
    key: "silent",
    label: `no GPS/event ${SILENT_MIN}m`,
    match: isSilent,
    flag: "silent",
    tone: "warn",
  },
  {
    group: "Other",
    key: "stale",
    label: `GPS > ${STALE_MIN}m`,
    match: (t) => t.leg !== "pending" && isStale(t),
    flag: "stale",
    tone: "warn",
  },
  { group: "Other", key: "backhaul", label: "backhaul opps", match: hasBackhaul, flag: "backhaul" },
];

/**
 * KPI counts follow the window, hub, client and search filters -- but not the status chips, which
 * are the thing the KPIs toggle. A chip that counted only what it had already narrowed to would
 * read the same on every board.
 */
export const kpiCounts = (trips: LiveBoardTrip[], f: BoardFilters): Record<string, number> => {
  const basis = trips.filter((t) => passes(t, f, "status"));
  const out: Record<string, number> = {};
  KPI_ITEMS.forEach((i) => {
    out[i.key] = basis.filter(i.match).length;
  });
  return out;
};

// ---------------------------------------------------------------------------------------------
// Filter state transitions
// ---------------------------------------------------------------------------------------------

/**
 * A status chip in the header narrows rather than toggles: with nothing narrowed yet, clicking
 * one means "just this status". Turning the last one off goes back to showing everything, which
 * is the only sane reading of an empty board.
 */
export const toggleKpiStatus = (statuses: Set<StatusKey>, status: StatusKey): Set<StatusKey> => {
  if (statuses.size === ALL_STATUSES.length) return new Set<StatusKey>([status]);
  const next = new Set(statuses);
  if (next.has(status)) {
    next.delete(status);
    if (!next.size) return new Set<StatusKey>(ALL_STATUSES);
  } else {
    next.add(status);
  }
  return next;
};

/** The side-panel chips are plain visibility switches -- no narrowing, no snap-back. */
export const toggleStatus = (statuses: Set<StatusKey>, status: StatusKey): Set<StatusKey> => {
  const next = new Set(statuses);
  if (next.has(status)) next.delete(status);
  else next.add(status);
  return next;
};

export const toggleFlag = (flags: Set<FlagKey>, flag: FlagKey): Set<FlagKey> => {
  const next = new Set(flags);
  if (next.has(flag)) next.delete(flag);
  else next.add(flag);
  return next;
};

export interface LayerToggles {
  hubs: boolean;
  labels: boolean;
  vehiclesOnly: boolean;
  backhaulMode: boolean;
}

// Hub pins and unit/trip labels start OFF. Forty hub names and a label on every mark is what the
// board looks like with nothing selected, and dispatch opens it to find trips, not to read the
// map furniture. Both stay one click away. This is also what a Clear resets to -- LiveTripsBoard
// calls this for the initial state AND for Clear filters, so the two agree by construction.
export const defaultLayers = (): LayerToggles => ({
  hubs: false,
  labels: false,
  vehiclesOnly: false,
  backhaulMode: false,
});

/** How many things the user has changed -- the number on the collapsed Filters bar. */
export const activeFilterCount = (f: BoardFilters, layers: LayerToggles): number =>
  [
    f.statuses.size < ALL_STATUSES.length,
    !!f.hubs,
    !!f.query,
    f.flags.size > 0,
    !!f.clients,
    layers.vehiclesOnly,
    layers.backhaulMode,
  ].filter(Boolean).length;

// ---------------------------------------------------------------------------------------------
// Derived option lists
// ---------------------------------------------------------------------------------------------

/**
 * The hubs the map can draw: the hub rows with usable coordinates, plus any hub a trip is
 * hanging off that the hub list did not carry, each with its trip count.
 */
export const computeHubs = (trips: LiveBoardTrip[], hubRows: LiveBoardHubRow[]): BoardHub[] => {
  const m = new Map<string, BoardHub>();
  hubRows.forEach((h) => {
    if (h.hub_lat != null && h.hub_lon != null && inConus(+h.hub_lon, +h.hub_lat)) {
      m.set(String(h.hubID), { id: String(h.hubID), name: h.hub, lat: h.hub_lat, lon: h.hub_lon, trips: 0 });
    }
  });
  trips.forEach((t) => {
    const id = String(t.hubID);
    if (!m.has(id) && t.hub_lat != null) {
      m.set(id, { id, name: t.hub, lat: t.hub_lat, lon: t.hub_lon, trips: 0 });
    }
    const h = m.get(id);
    if (h) h.trips += 1;
  });
  return [...m.values()];
};

/**
 * Every hub that can be picked, including ones whose coordinates were thrown out -- a hub with
 * trips on the board has to stay filterable even when it cannot be drawn.
 */
export const hubOptions = (trips: LiveBoardTrip[], hubs: BoardHub[]): { value: string; label: string }[] => {
  const names = new Map<string, string>();
  trips.forEach((t) => names.set(String(t.hubID), t.hub || ""));
  hubs.forEach((h) => names.set(h.id, h.name || ""));
  return [...names.entries()]
    .sort((a, b) => compareText(a[1], b[1]))
    .map(([value, label]) => ({ value, label }));
};

/** Clients by trip count desc, so the option order holds still while the counts change. */
export const clientOptions = (trips: LiveBoardTrip[]): { value: string; label: string }[] => {
  const m = new Map<string, number>();
  trips.forEach((t) => {
    const c = t.client || "";
    m.set(c, (m.get(c) || 0) + 1);
  });
  return [...m.entries()]
    .sort((a, b) => b[1] - a[1] || compareText(a[0], b[0]))
    .map(([value]) => ({ value, label: value || "(none)" }));
};

// ---------------------------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------------------------

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * Parses a board timestamp without letting the browser's zone into it.
 *
 * The board carries two kinds of stamp and they have to be told apart by shape, because nothing
 * else distinguishes them. The `*_utc` columns arrive ISO-8601 with a `Z`. `pickup_local` and
 * `last_event_time_local` arrive with no zone marker at all, on purpose: they are trip-local wall
 * clock, and a 14:00 pickup in Amarillo is 14:00 whoever is reading it. An unmarked stamp is
 * therefore pinned to UTC so that reading its UTC fields back gives the wall clock unchanged --
 * letting the browser read it as local would move every one of them by the reader's own offset.
 */
export const parseTimestamp = (s: string): Date | null => {
  if (!s) return null;
  const iso = s.replace(" ", "T");
  const d = new Date(/([zZ]|[+-]\d{2}:?\d{2})$/.test(iso) ? iso : `${iso}Z`);
  return isNaN(d.getTime()) ? null : d;
};

/** Milliseconds for sorting. Lexicographic compare is not safe once fractional seconds vary. */
export const timeValue = (s: string): number | null => {
  const d = parseTimestamp(s);
  return d ? d.getTime() : null;
};

/**
 * Renders a timestamp exactly as it labels itself.
 *
 * A UTC column reads as UTC and a trip-local column reads as the trip's own clock -- reading back
 * the UTC fields of what parseTimestamp produced is what keeps both of them where they belong.
 */
export const fmtLocal = (s: string): string => {
  const d = parseTimestamp(s);
  if (!d) return "—";
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()} ${String(d.getUTCHours()).padStart(2, "0")}:${String(
    d.getUTCMinutes()
  ).padStart(2, "0")}`;
};

/** Signed minutes as "in 2h 05m" / "35m ago". */
export const fmtRel = (min: number): string => {
  if (min == null) return "—";
  const a = Math.abs(Math.round(min));
  const h = Math.floor(a / 60);
  const mm = a % 60;
  const s = a >= 60 * 24 ? `${Math.floor(a / 1440)}d ${h % 24}h` : h ? `${h}h ${String(mm).padStart(2, "0")}m` : `${mm}m`;
  return min < 0 ? `${s} ago` : `in ${s}`;
};

/** An age in minutes. Negative means the device's clock is ahead of ours, not that it is early. */
export const fmtAgo = (min: number): string => (min == null ? "—" : min < 0 ? "clock ahead" : fmtRel(-min));

/**
 * The trip screen this board links to. Trip number when there is one, id otherwise -- the same
 * choice the standalone board made when it linked back into Flow from outside.
 */
export const tripHref = (t: LiveBoardTrip): string => `/trips/${encodeURIComponent(String(t.tripNumber || t.tripID))}`;
